import { FC, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportPaymentsByCustomerCountryData,
  TReportPaymentsByCustomerCityOrCountryPayload,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import {
  exportPaymentsByCustomerCountryReport,
  getPaymentsByCustomerCountryReport,
} from '@src/services/reports';
import { buildCommonPayloadWithGroupingAndProducts } from '@src/utils/buildCommonPayload';
import { toast } from 'react-toastify';
import { downloadFile } from '@src/lib/utils';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

export const PaymentsByCustomerCountry: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<
    TReportPaymentsByCustomerCountryData[]
  >([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
    enableCountries: false,
  });

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } =
    useReportTableColumns<TReportPaymentsByCustomerCountryData>({
      data,
      selectedItems: [],
      compareToPreviousPeriod: filters.compareToPreviousPeriod,
      compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
      getAccessField: (item) => item.date,
      getMainDataField: (item, key) => item.countries[key] || 0,
      getPreviousPeriodField: (item, key) => item.countries_previous?.[key] || 0,
      getPreviousYearField: (item, key) =>
        item.countries_previous_year?.[key] || 0,
      itemsField: 'countries',
      columnTitleFormatter: (key) => key.toUpperCase(),
      valueFormatter: (_, value) => `$${value.toLocaleString()}`,
      includeTotalsRow: true,
    });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.countries[key] || 0,
    getPreviousPeriodField: (item, key) => item.countries_previous?.[key],
    getPreviousYearField: (item, key) => item.countries_previous_year?.[key],
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const commonPayload = buildCommonPayloadWithGroupingAndProducts(filters);
      const payload: TReportPaymentsByCustomerCityOrCountryPayload = {
        ...commonPayload,
        by_city: false,
        cities: [],
      };

      const response = await getPaymentsByCustomerCountryReport(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to generate report.');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const commonPayload = buildCommonPayloadWithGroupingAndProducts(filters);
      const payload: TReportPaymentsByCustomerCityOrCountryPayload = {
        ...commonPayload,
        by_city: false,
        cities: [],
      };

      const response = await exportPaymentsByCustomerCountryReport(payload);

      downloadFile(response.data, 'payments-by-customer-country');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report.');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Payments By Customer Country Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showGroupByFilter
          showPeriodFilter
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #3D3D3D',
                  borderRadius: '8px',
                  boxShadow: '0px 7px 13px 0px #B6D72C14',
                }}
                formatter={(value: number) => `$${value.toLocaleString()}`}
              />
              {displayItems.map((country, index) => (
                <Line
                  key={country}
                  activeDot={{ r: 6 }}
                  dataKey={country}
                  dot={{ r: 4 }}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}
              {filters.compareToPreviousPeriod &&
                displayItems.map((country, index) => (
                  <Line
                    key={`${country}_previous_period`}
                    dataKey={`${country}_previous_period`}
                    dot={{ r: 3 }}
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((country, index) => (
                  <Line
                    key={`${country}_previous_year`}
                    dataKey={`${country}_previous_year`}
                    dot={{ r: 3 }}
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
